import React, { useEffect, useState } from 'react';
import { Tooltip, CircularProgress, Box, IconButton, Icon } from '@mui/material';
import { InfraPlan } from '../../../models/plan/plan';
import { planManagementService } from '../../../services/plan-management.service';
import { contentService } from '../../../services/content.service';
import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { authService } from '../../../services/auth.service';

interface BeoordelingenDetailPanelProps {
  plan: InfraPlan;
  mijnOrganisatie: MijnOrganisatieListItem | null;
  onEditBeoordeling?: (plan: InfraPlan) => void;
  refreshKey?: number;
}

const BeoordelingenDetailPanel: React.FC<BeoordelingenDetailPanelProps> = ({ 
  plan, 
  mijnOrganisatie,
  onEditBeoordeling,
  refreshKey = 0,
}) => {
  const [beoordelingen, setBeoordelingen] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBeoordelingenData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!plan.infplId) {
          setBeoordelingen([]);
          setLoading(false);
          return;
        }

        const result = await planManagementService.getPlanBeoordelingenCurrent(plan.infplId) as any;
        
        if (result && result.planBeoordelingen) {
          // Sort beoordelingen inline
          const statusOrder: Record<string, number> = {
            wel_belang: 0,
            mogelijk_belang: 1,
            geen_belang: 2,
            nog_niet_beoordeeld: 3,
          };
          const sorted = [...result.planBeoordelingen].sort((a, b) => {
            // Put "mijnOrganisatie" first
            if (mijnOrganisatie?.id) {
              const isA_OwnOrganisation = a.planBeoordelingOrganizationId === mijnOrganisatie.id;
              const isB_OwnOrganisation = b.planBeoordelingOrganizationId === mijnOrganisatie.id;

              if (isA_OwnOrganisation && !isB_OwnOrganisation) return -1;
              if (!isA_OwnOrganisation && isB_OwnOrganisation) return 1;
            }

            // Then: sort by status order
            const orderA = statusOrder[a.key?.id] ?? 99;
            const orderB = statusOrder[b.key?.id] ?? 99;
            if (orderA !== orderB) return orderA - orderB;

            // Finally: alphabetically
            return a.planBeoordelingOrganizationName.localeCompare(
              b.planBeoordelingOrganizationName,
              'nl',
              { sensitivity: 'base' }
            );
          });
          setBeoordelingen(sorted);
        } else {
          setBeoordelingen([]);
        }
      } catch (err) {
        console.error('Error fetching beoordelingen:', err);
        setError('Fout bij ophalen beoordelingen');
        setBeoordelingen([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBeoordelingenData();
  }, [plan.infplId, mijnOrganisatie?.id, refreshKey]);

  const getBeoordelingIcon = (key: string) => {
    return contentService.getBeoordelingStatusIcon(key);
  };

  const getBeoordelingTooltip = (key: string) => {
    return contentService.getBeoordelingStatusDescription(key);
  };

  const getBeoordelingStatusLabel = (key: string) => {
    return contentService.getBeoordelingStatusLabel(key);
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" padding={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={2}>
        <p className="error-message">{error}</p>
      </Box>
    );
  }

  if (beoordelingen.length === 0) {
    return (
      <Box padding={2}>
        <p>Geen beoordelingen beschikbaar voor dit plan</p>
      </Box>
    );
  }

  return (
    <div className="beoordeling-detail-list">
      {beoordelingen.map((item: any, index: number) => {
        const isMijn = !!(mijnOrganisatie && item.planBeoordelingOrganizationId === mijnOrganisatie.id);
        const statusKey = item.key.id;
        return (
          <div
            key={index}
            className={`beoordeling-detail-row ${statusKey}${isMijn ? ' mijn_organisatie' : ''}`}
          >
            <Tooltip title={getBeoordelingTooltip(statusKey)} placement="top" arrow>
              <span className={`beoordeling-icon ${statusKey}`} style={{ flexShrink: 0 }}>
                <span className="material-icons">{getBeoordelingIcon(statusKey)}</span>
              </span>
            </Tooltip>

            <span className="beoordeling-detail-org">
              {item.planBeoordelingOrganizationName}
            </span>

            <span className="beoordeling-detail-status">
              {getBeoordelingStatusLabel(statusKey)}
            </span>

            {statusKey !== 'nog_niet_beoordeeld' && item.timestamp && (
              <span className="beoordeling-detail-date">
                {new Date(item.timestamp).toLocaleDateString('nl-NL', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}

            {isMijn && onEditBeoordeling && authService.canCrud() && (
              <Tooltip title={statusKey !== 'nog_niet_beoordeeld' ? 'Beoordeling wijzigen' : 'Beoordelen'} arrow>
                <IconButton
                  size="small"
                  color="secondary"
                  className="beoordeling-detail-edit"
                  onClick={() => onEditBeoordeling(plan)}
                >
                  <Icon>edit</Icon>
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BeoordelingenDetailPanel;
